import React, { useEffect, useMemo, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import {
  Box,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ButtonComponent from "../ui/ButtonComponent";
import { useToast } from "../ui/ToastContext";
import { v4 as uuidv4 } from "uuid";
import SLOT_MAP from "./annotationsConfig";
import "./MultiPagePDFViewer.css";
// FIREBASE IMPORTS
import { auth, db } from "../../firebase-config";
import { collection, doc, onSnapshot } from "firebase/firestore";
import axios from "axios";

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const SUGGEST_ANNOTATIONS_URL =
  "https://getannotationsuggestions-774073159618.us-central1.run.app";

/* ----------------------------- Helpers ----------------------------- */

function safeString(v) {
  if (v == null) return "";
  return String(v);
}

function normalizeWhitespace(s) {
  return safeString(s).replace(/\s+/g, " ").trim();
}

function stableKeyForSuggestion(s) {
  const t = normalizeWhitespace(
    `${s?.type || ""}|${s?.title || ""}|${s?.text || ""}|${s?.category || ""}|${s?.subsection || ""}`
  );
  return t.toLowerCase();
}

function clampInt(n, min, max) {
  const x = Math.floor(Number(n) || 0);
  return Math.max(min, Math.min(max, x));
}

function formatMMDDYYYY(dateObj) {
  const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
  const dd = String(dateObj.getDate()).padStart(2, "0");
  const yyyy = String(dateObj.getFullYear());
  return `${mm}/${dd}/${yyyy}`;
}

function toISODateValue(dateObj) {
  const yyyy = String(dateObj.getFullYear());
  const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
  const dd = String(dateObj.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function parseISODateValue(iso) {
  const m = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  const dt = new Date(y, mo, d);
  if (Number.isNaN(dt.getTime())) return null;
  return dt;
}

function applyLabelDate(text, mmddyyyy) {
  const raw = safeString(text);
  if (!raw) return raw;
  if (raw.includes("[Date Placeholder]")) {
    return raw.replaceAll("[Date Placeholder]", mmddyyyy);
  }
  if (raw.match(/ - \d{2}\/\d{2}\/\d{4}$/)) return raw;
  return `${raw} - ${mmddyyyy}`.trim();
}

function generateStableDocId(url) {
  if (!url) return null;
  let hash = 0;
  for (let i = 0; i < url.length; i++) {
    const char = url.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return `pdf_${Math.abs(hash).toString(16)}`;
}

function getAnnotationCategory(typeOrSlot) {
  const t = (typeOrSlot || "").toLowerCase();
  if (t === "header") return "header";
  if (t === "label") return "label";
  if (t === "standardnotation" || t.startsWith("notation")) return "standardNotation";
  return "label";
}

/* ----------------------------- Main Component ----------------------------- */

const MultiPagePDFViewer = ({
  fileUrl,
  documentId,
  annotations,
  setAnnotations,
  selectedAnnotation,
  onAnnotationPlaced,
  onSave,
}) => {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  // --- STATE DECLARATIONS ---
  const [orgId, setOrgId] = useState(null); // <-- This is what was missing!
  const [useIntroLabel, setUseIntroLabel] = useState(false);
  const [autoPlaceEnabled, setAutoPlaceEnabled] = useState(true);

  const [isSuggestLoading, setIsSuggestLoading] = useState(false);
  const [suggestError, setSuggestError] = useState("");
  const [allSuggestions, setAllSuggestions] = useState({});
  const [progressStatus, setProgressStatus] = useState(null);

  const allSuggestionsRef = useRef(allSuggestions);
  useEffect(() => {
    allSuggestionsRef.current = allSuggestions;
  }, [allSuggestions]);

  const [attemptedAutoPlaceIds, setAttemptedAutoPlaceIds] = useState(new Set());
  const [evaluatedPage, setEvaluatedPage] = useState(0); 
  const [isSlotCalcPending, setIsSlotCalcPending] = useState(true);
  const [bestNotationSlot, setBestNotationSlot] = useState("notationA");

  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [labelModalDate, setLabelModalDate] = useState(toISODateValue(new Date()));
  const [pendingLabelSuggestion, setPendingLabelSuggestion] = useState(null);

  const { addToast } = useToast();
  const pageCanvasRef = useRef(null);
  const pageViewBoxRef = useRef(null);

  /* --- AUTH LISTENER: Safely extracts orgId from token claims --- */
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const tokenResult = await user.getIdTokenResult();
          setOrgId(tokenResult.claims.organizationId || tokenResult.claims.orgId || null);
        } catch (error) {
          console.error("Failed to fetch token claims", error);
        }
      } else {
        setOrgId(null);
      }
    });
    return () => unsubscribe();
  }, []);

  const getCurrentPageDate = () => {
    // Read from the Ref to bypass the stale closure in the useEffect
    const pageData = allSuggestionsRef.current[currentPage.toString()];
    const extractedDate = pageData?.visual?.chartDate;
    
    // Loosened regex slightly to safely catch single-digit months/days (e.g. 7/6/2026)
    if (extractedDate && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(extractedDate)) {
      return extractedDate;
    }
    
    return formatMMDDYYYY(new Date()); 
  };

  /* --- TOGGLE HANDLER --- */
  const handleToggleSessionLabel = (e) => {
      const isIntro = e.target.checked;
      setUseIntroLabel(isIntro);
      
      setAnnotations(prev => prev.map(a => {
          if (a.type === 'label' && a.text) {
              const newText = isIntro 
                  ? a.text.replace(/Orientation Session/gi, "Intro Session") 
                  : a.text.replace(/Intro Session/gi, "Orientation Session");
              return { ...a, text: newText };
          }
          return a;
      }));
  };

  /* --- DIMENSION PARSING LOGIC --- */
  function getPageSizePoints() {
    const vb = pageViewBoxRef.current;
    if (!vb || vb.length !== 4) return null;
    return { wPts: vb[2] - vb[0], hPts: vb[3] - vb[1] };
  }

  function resolveDimension(val, canvasDim, ptsDim) {
    if (typeof val === "string" && val.endsWith("%")) {
      return (parseFloat(val) / 100) * canvasDim;
    }
    const n = parseFloat(val) || 0;
    if (n > 0 && n <= 1) return n * canvasDim; 
    return n * (canvasDim / ptsDim); 
  }

  function getBoundingBoxPx(box, canvasWidth, canvasHeight, ptsW, ptsH) {
    if (!box) return { x: 0, y: 0, w: 0, h: 0 };
    return {
      x: resolveDimension(box.x, canvasWidth, ptsW),
      y: resolveDimension(box.y, canvasHeight, ptsH),
      w: resolveDimension(box.w, canvasWidth, ptsW),
      h: resolveDimension(box.h, canvasHeight, ptsH),
    };
  }

  const resolvedDocId = useMemo(() => {
    return documentId || generateStableDocId(fileUrl);
  }, [documentId, fileUrl]);

  useEffect(() => {
    setCurrentPage(1);
  }, [fileUrl]);

  /* ----------------------------- Manual Placement Listener ----------------------------- */
  useEffect(() => {
    if (!selectedAnnotation) return;

    const effectiveType =
      selectedAnnotation.type ||
      (selectedAnnotation.slot && selectedAnnotation.slot.startsWith("notation")
        ? "standardNotation"
        : "label");
    const incomingCategory = getAnnotationCategory(effectiveType);

    setAnnotations((prev) => {
      const next = [...prev];
      const idsToRemove = new Set();

      const itemsToRemove = next.filter((a) => {
        const cat = getAnnotationCategory(a.type || a.slot);
        return a.page === currentPage && cat === incomingCategory;
      });

      itemsToRemove.forEach((rem) => {
        if (rem.autoPlacedSuggestionId) idsToRemove.add(rem.autoPlacedSuggestionId);
      });

      if (idsToRemove.size > 0) {
        setAttemptedAutoPlaceIds((current) => {
          const nextSet = new Set(current);
          idsToRemove.forEach((id) => nextSet.delete(id));
          return nextSet;
        });
      }

      const filtered = next.filter((a) => {
        const cat = getAnnotationCategory(a.type || a.slot);
        return !(a.page === currentPage && cat === incomingCategory);
      });

      let finalLabelText = selectedAnnotation.text || "";
      if (effectiveType === 'label') {
        finalLabelText = applyLabelDate(finalLabelText, getCurrentPageDate());
      }

      filtered.push({
        id: uuidv4(),
        page: currentPage,
        type: effectiveType,
        slot: selectedAnnotation.slot || "label",
        title: selectedAnnotation.title || null,
        text: finalLabelText,
        includeTitle: effectiveType === "header",
        ...(SLOT_MAP[selectedAnnotation.slot] || SLOT_MAP["label"]),
        category: selectedAnnotation.category,
        subsection: selectedAnnotation.subsection,
      });

      return filtered;
    });

    if (onAnnotationPlaced) onAnnotationPlaced();
  }, [selectedAnnotation, currentPage, setAnnotations, onAnnotationPlaced]); 

  /* ----------------------------- Orchestrator & Listeners ----------------------------- */
  useEffect(() => {
    async function triggerOrchestrator() {
      if (!fileUrl || !resolvedDocId || !auth.currentUser || !orgId) return;

      setIsSuggestLoading(true);
      setSuggestError("");

      try {
        const idToken = await auth.currentUser.getIdToken(true);

        axios
          .post(
            SUGGEST_ANNOTATIONS_URL,
            {
              organizationId: orgId,
              fileUrl,
              documentId: resolvedDocId,
              processFullDocument: true,
            },
            { headers: { Authorization: `Bearer ${idToken}` } }
          )
          .catch((e) => console.warn("Background process active:", e));
      } catch (e) {
        console.error("Failed to trigger orchestrator:", e);
        setSuggestError("Could not start suggestion engine.");
      }
    }

    triggerOrchestrator();
  }, [fileUrl, resolvedDocId, orgId]);

  useEffect(() => {
    if (!resolvedDocId || !orgId) return;

    setAllSuggestions({});
    setProgressStatus(null);
    setIsSuggestLoading(true);

    const suggestionsRef = collection(
      db,
      "organizations",
      orgId,
      "documents",
      resolvedDocId,
      "suggestions"
    );

    const unsubSuggestions = onSnapshot(
      suggestionsRef,
      (snapshot) => {
        const map = {};
        snapshot.forEach((d) => {
          map[d.id] = d.data();
        });
        setAllSuggestions(map);

        if (!snapshot.empty) setIsSuggestLoading(false);
      },
      (error) => {
        console.error("Firestore listener error:", error);
      }
    );

    const docRef = doc(db, "organizations", orgId, "documents", resolvedDocId);
    const unsubDoc = onSnapshot(docRef, (docSnap) => {
      if (!docSnap.exists()) return;
      const data = docSnap.data();
      if (data.processingProgress) setProgressStatus(data.processingProgress);
    });

    return () => {
      unsubSuggestions();
      unsubDoc();
    };
  }, [resolvedDocId, orgId]);

  // ----------------------------
  // Normalize suggestions
  // ----------------------------
  const suggestedList = useMemo(() => {
    const pageData = allSuggestions[currentPage.toString()] || null;
    const s = pageData?.suggestions || pageData || null;

    if (!s) return [];

    const pickTopCandidate = (arr) => {
      if (!Array.isArray(arr) || !arr.length) return null;
      const first = arr[0];
      const cand = first?.candidate || first;
      if (!cand) return null;

      return {
        type: cand.type || null,
        title: cand.title || null,
        text: cand.text || null,
        includeTitle: cand.type === "header",
        confidence: typeof first.score === "number" ? first.score : null,
        category: cand.category || null,
        subsection: cand.subsection || null,
        annotationLibraryId:
          cand.annotationLibraryId || cand.stableHashId || cand.stableId || null,
      };
    };

    const out = [];

    const headerTop = pickTopCandidate(s.header);
    if (headerTop) out.push({ ...headerTop, type: "header", slot: "header" });

    const labelTop = pickTopCandidate(s.label);
    if (labelTop) {
        let text = labelTop.text || "";
        if (useIntroLabel) {
            text = text.replace(/Orientation Session/gi, "Intro Session");
        } else {
            text = text.replace(/Intro Session/gi, "Orientation Session");
        }
        out.push({ ...labelTop, text, type: "label", slot: "label" });
    }

    const sn = s.standardNotation || {};
    const fallbackArr = sn.notationA || sn.notationB || sn.notationC || sn.notationD || [];
    const bestArr = sn[bestNotationSlot] && sn[bestNotationSlot].length > 0 ? sn[bestNotationSlot] : fallbackArr;
    const stdTop = pickTopCandidate(bestArr);

    if (stdTop) out.push({ ...stdTop, type: "standardNotation", slot: bestNotationSlot });

    return out
      .filter(Boolean)
      .map((x) => ({
        id: `p${currentPage}_${stableKeyForSuggestion(x)}`,
        ...x,
      }))
      .filter((x) => x.type && x.slot && (x.text || x.title));
  }, [allSuggestions, currentPage, bestNotationSlot, useIntroLabel]);

  const hasSuggestions = suggestedList.length > 0;

  // ----------------------------
  // AUTO-PLACEMENT LOGIC
  // ----------------------------
  useEffect(() => {
    // Abort if the user has toggled off auto-placement
    if (!autoPlaceEnabled) return;

    if (evaluatedPage !== currentPage || !suggestedList.length) return;

    const highConfidenceItems = suggestedList.filter(s => {
      if (s.type === 'header') return s.confidence >= 0.75;
      return s.confidence >= 0.80;
    });
    if (highConfidenceItems.length === 0) return;

    let appliedCount = 0;

    setAnnotations((prev) => {
      let next = [...prev];
      const idsAttempted = new Set();

      highConfidenceItems.forEach((s) => {
        const incomingCategory = getAnnotationCategory(s.type);

        const isCategoryOccupied = next.some(
          (a) =>
            a.page === currentPage &&
            getAnnotationCategory(a.type || a.slot) === incomingCategory
        );

        const alreadyAttempted = attemptedAutoPlaceIds.has(s.id);

        if (!isCategoryOccupied && !alreadyAttempted) {
          const slotConfig = SLOT_MAP[s.slot];
          if (slotConfig) {
            
            let finalText = s.text;
            if (s.type === 'label') {
                finalText = applyLabelDate(s.text, getCurrentPageDate());
            }

            next.push({
              id: uuidv4(),
              page: currentPage,
              type: s.type,
              slot: s.slot,
              title: s.title,
              text: finalText,
              includeTitle: s.type === "header",
              category: s.category,
              subsection: s.subsection,
              annotationLibraryId: s.annotationLibraryId || null,
              suggested: true,
              autoPlacedSuggestionId: s.id,
              confidence: s.confidence,
              ...slotConfig,
            });
            idsAttempted.add(s.id);
            appliedCount++;
          }
        }
      });

      if (appliedCount > 0) {
        setAttemptedAutoPlaceIds((prevSet) => {
          const newSet = new Set(prevSet);
          idsAttempted.forEach((id) => newSet.add(id));
          return newSet;
        });
        return next;
      }

      return prev;
    });
  }, [suggestedList, currentPage, attemptedAutoPlaceIds, setAnnotations, evaluatedPage]);

  // ----------------------------
  // Actions
  // ----------------------------

  const onDocumentLoadSuccess = ({ numPages: n }) => {
    setNumPages(n);
  };

  const handleSlotChange = (id, newSlot) => {
    setAnnotations((prev) => {
      const updated = prev.map((a) =>
        a.id === id
          ? {
              ...a,
              ...SLOT_MAP[newSlot],
              slot: newSlot,
              autoPlacedSuggestionId: null, 
            }
          : a
      );

      const moved = updated.find((a) => a.id === id);
      if (!moved) return prev;

      const filtered = updated.filter(
        (a) =>
          a.id === id ||
          !(
            a.page === moved.page &&
            getAnnotationCategory(a.type) === getAnnotationCategory(moved.type) &&
            a.id !== id
          )
      );

      return filtered;
    });
  };

  const handleDeleteAnnotation = (id) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== id));
  };

  const handleSaveAnnotations = async () => {
    setIsLoading(true);

    if (!auth.currentUser || !orgId) {
      alert("You must be logged in to save annotations.");
      setIsLoading(false);
      return;
    }

    try {
      const idToken = await auth.currentUser.getIdToken(true);

      await axios.post(
        "https://saveannotations-774073159618.us-central1.run.app",
        {
          fileUrl,
          annotations,
          organizationId: orgId,
          mode: "saveOnly",
        },
        {
          headers: { Authorization: `Bearer ${idToken}` },
        }
      );

      setIsLoading(false);
      addToast("Annotations saved successfully.", "success");
    } catch (err) {
      console.error("Save failed:", err);
      setIsLoading(false);
      addToast("Failed to save annotations.", "error");
    }
  };

  const applyAnnotationToPage = (s, overrides = {}) => {
    const slot = SLOT_MAP[s.slot];
    if (!slot) return;

    setAnnotations((prev) => {
      const next = [...prev];
      const incomingCategory = getAnnotationCategory(s.type);

      const filtered = next.filter(
        (a) =>
          !(
            a.page === currentPage &&
            getAnnotationCategory(a.type || a.slot) === incomingCategory
          )
      );

      filtered.push({
        id: uuidv4(),
        page: currentPage,
        type: s.type,
        slot: s.slot,
        title: s.title,
        text: s.text,
        includeTitle: s.type === "header",
        category: s.category,
        subsection: s.subsection,
        annotationLibraryId: s.annotationLibraryId || null,
        suggested: true,
        confidence: s.confidence,
        ...slot,
        ...overrides,
      });

      return filtered;
    });
  };

  const applyOneSuggested = (s) => {
    if (!s) return;
    let safeObj = { ...s };

    if (s.type === "label") {
      safeObj.text = applyLabelDate(s.text, getCurrentPageDate());
    }

    applyAnnotationToPage(safeObj);
    addToast("Suggestion applied.", "success");
  };

  const applyAllSuggested = () => {
    if (!suggestedList.length) return;
    
    suggestedList.forEach((s) => {
      if (s.type === "label") {
        const updatedText = applyLabelDate(s.text, getCurrentPageDate());
        applyAnnotationToPage({ ...s, text: updatedText });
      } else {
        applyAnnotationToPage(s);
      }
    });

    addToast("Suggestions applied to this page.", "success");
  };

  const handleCancelLabelDate = () => {
    setLabelModalOpen(false);
    setPendingLabelSuggestion(null);
  };

  const handleConfirmLabelDate = () => {
    const s = pendingLabelSuggestion;
    if (!s) {
      setLabelModalOpen(false);
      return;
    }

    const dt = parseISODateValue(labelModalDate) || new Date();
    const formatted = formatMMDDYYYY(dt);
    const updatedText = applyLabelDate(s.text, formatted);

    applyAnnotationToPage({ ...s, text: updatedText }, { includeTitle: false });

    setLabelModalOpen(false);
    setPendingLabelSuggestion(null);
    addToast("Label applied with date.", "success");
  };

  /* ----------------------------- PIXEL DENSITY (SLOTS) ----------------------------- */

  function getCanvasFromPage() {
    return pageCanvasRef.current?.querySelector("canvas") || null;
  }

  function computeRegionDensity(canvas, regionPx) {
    if (!canvas) return Number.POSITIVE_INFINITY;
    
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    if (!ctx) return Number.POSITIVE_INFINITY;

    const x = clampInt(regionPx.x, 0, canvas.width - 1);
    const y = clampInt(regionPx.y, 0, canvas.height - 1);
    const w = clampInt(regionPx.w, 1, canvas.width - x);
    const h = clampInt(regionPx.h, 1, canvas.height - y);

    if (w <= 0 || h <= 0) return Number.POSITIVE_INFINITY;

    try {
      const img = ctx.getImageData(x, y, w, h).data;
      let dense = 0;
      const step = 16; 

      for (let i = 0; i < img.length; i += step) {
        const r = img[i];
        const g = img[i + 1];
        const b = img[i + 2];
        const a = img[i + 3];

        if (a < 10) continue;

        const isBackground = r > 220 && g > 220 && b > 220;
        if (!isBackground) dense++;
      }

      return dense / Math.max(1, img.length / step);
    } catch (err) {
      console.warn("Canvas tainted or getImageData failed:", err);
      return Number.POSITIVE_INFINITY;
    }
  }

  function calculateBestNotationSlot() {
    const canvas = getCanvasFromPage();
    if (!canvas) return "notationA";

    const candidateSlots = ["notationA", "notationB", "notationC", "notationD"].filter(
      (k) => SLOT_MAP[k]
    );

    const sizePts = getPageSizePoints() || { wPts: 612, hPts: 792 };
    let scores = {};

    for (const slotKey of candidateSlots) {
      const regionPts = SLOT_MAP[slotKey];
      const sBox = getBoundingBoxPx(regionPts, canvas.width, canvas.height, sizePts.wPts, sizePts.hPts);
      scores[slotKey] = computeRegionDensity(canvas, sBox); 
    }

    const OPEN_THRESHOLD = 0.05; 

    const pageData = allSuggestionsRef.current[currentPage.toString()];
    const chartTitle = pageData?.visual?.chartTitle || "";
    const isHarmony = chartTitle.toLowerCase().includes("harmony");

    if (isHarmony) {
        const cScore = scores["notationC"];
        const dScore = scores["notationD"];
        
        const cOpen = cScore !== undefined && cScore <= OPEN_THRESHOLD;
        const dOpen = dScore !== undefined && dScore <= OPEN_THRESHOLD;

        if (cOpen && dOpen) {
            return cScore <= dScore ? "notationC" : "notationD";
        } else if (cOpen) {
            return "notationC";
        } else if (dOpen) {
            return "notationD";
        }
    } else {
        if (scores["notationA"] !== undefined && scores["notationA"] <= OPEN_THRESHOLD) {
            return "notationA";
        }
    }

    let best = candidateSlots[0] || "notationA";
    let bestScore = Number.POSITIVE_INFINITY;

    for (const slotKey of candidateSlots) {
      if (scores[slotKey] < bestScore) {
        bestScore = scores[slotKey];
        best = slotKey;
      }
    }

    return best;
  }

  useEffect(() => {
    let cancelled = false;
    setIsSlotCalcPending(true);

    async function computeWhenReady() {
      if (currentPage === 1) {
        setBestNotationSlot("notationA");
        setEvaluatedPage(currentPage);
        setIsSlotCalcPending(false);
        return;
      }

      const maxTries = 15;
      const delayMs = 150;

      for (let i = 0; i < maxTries; i++) {
        if (cancelled) return;

        const canvas = getCanvasFromPage();
        const hasViewBox = !!(pageViewBoxRef.current && pageViewBoxRef.current.length === 4);

        if (canvas && canvas.width > 0 && hasViewBox) {
          await new Promise((r) => setTimeout(r, 200));
          if (cancelled) return;

          const best = calculateBestNotationSlot();
          setBestNotationSlot(best);
          setEvaluatedPage(currentPage); 
          setIsSlotCalcPending(false);
          return;
        }

        await new Promise((r) => setTimeout(r, delayMs));
      }

      if (!cancelled) {
          setBestNotationSlot("notationA");
          setEvaluatedPage(currentPage);
          setIsSlotCalcPending(false);
      }
    }

    computeWhenReady();
    return () => {
      cancelled = true;
    };
  }, [fileUrl, currentPage]);

  /* ----------------------------- Render ----------------------------- */

  return (
    <Box className="pdf-container">
      {/* Sticky Suggestion Panel */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          width: "100%",
          maxWidth: 980,
          margin: "0 auto",
          mb: 2,
          p: 2,
          borderBottom: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
          boxShadow: "0px 2px 4px rgba(0,0,0,0.05)",
        }}
      >
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
          <Box>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              Suggested for Page {currentPage}
            </div>
            <div style={{ fontSize: 12, color: "#666" }}>
              {progressStatus && progressStatus.status === "processing"
                ? `Analyzing page ${progressStatus.current} of ${progressStatus.total}...`
                : isSlotCalcPending 
                    ? "Calculating optimal placement..." 
                    : "Suggestions are best-effort. Users can adjust slots or delete before saving."}
            </div>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            
            {/* NEW: Auto-Place Toggle */}
            <FormControlLabel
                control={
                    <Switch 
                        checked={autoPlaceEnabled} 
                        onChange={(e) => setAutoPlaceEnabled(e.target.checked)} 
                        size="small" 
                        color="primary"
                    />
                }
                label={<span style={{ fontSize: 12, color: "#666", fontWeight: 600 }}>Auto-Place</span>}
                sx={{ m: 0, mr: 1 }}
            />
            
            <FormControlLabel
                control={
                    <Switch 
                        checked={useIntroLabel} 
                        onChange={handleToggleSessionLabel} 
                        size="small" 
                        color="primary"
                    />
                }
                label={<span style={{ fontSize: 12, color: "#666", fontWeight: 600 }}>Use "Intro" Label</span>}
                sx={{ m: 0, mr: 2 }}
            />
            <Button
              variant="outlined"
              disabled={currentPage === 1 || isSuggestLoading || !hasSuggestions || isSlotCalcPending}
              onClick={applyAllSuggested}
            >
              {isSuggestLoading ? "Processing..." : "Accept All"}
            </Button>
          </Box>
        </Box>

        {progressStatus && progressStatus.status === "processing" && (
          <Box mt={1} width="100%">
            <LinearProgress
              variant="determinate"
              value={(progressStatus.current / progressStatus.total) * 100}
            />
          </Box>
        )}

        {suggestError ? (
          <div style={{ marginTop: 10, color: "#d32f2f", fontSize: 13 }}>
            {suggestError}
          </div>
        ) : null}

        {!isSuggestLoading && currentPage !== 1 && hasSuggestions ? (
          <Box mt={1.25} display="flex" flexDirection="column" gap={0.75}>
            {suggestedList.map((s, idx) => {
              const conf =
                typeof s.confidence === "number" ? `${Math.round(s.confidence * 100)}%` : "—";
              
              const matchingAnnotation = annotations.find((a) => {
                if (a.page !== currentPage) return false;
                if (a.autoPlacedSuggestionId && a.autoPlacedSuggestionId === s.id) return true;
                
                const aText = normalizeWhitespace(a.text || a.title || "").replace(/ - \d{2}\/\d{2}\/\d{4}$/, "");
                const sText = normalizeWhitespace(s.text || s.title || "");
                
                return getAnnotationCategory(a.type || a.slot) === getAnnotationCategory(s.type || s.slot) &&
                       aText === sText;
              });
              
              const displaySlot = matchingAnnotation && matchingAnnotation.slot ? matchingAnnotation.slot : s.slot;
              
              const label = s.type === "standardNotation"
                  ? `Standard Notation (${displaySlot})`
                  : s.type.charAt(0).toUpperCase() + s.type.slice(1);
                  
              const primaryText = s.type === "label" ? s.text : s.title || "(Untitled)";
              const isAutoPlaced = matchingAnnotation && !!matchingAnnotation.autoPlacedSuggestionId;
              const isManuallyAccepted = matchingAnnotation && !matchingAnnotation.autoPlacedSuggestionId;

              return (
                <Box
                  key={`${s.id}_${idx}`}
                  sx={{
                    p: 1,
                    border: "1px solid #eee",
                    borderRadius: "8px",
                    backgroundColor: isAutoPlaced ? "#f0f7ff" : "#fff",
                    transition: "background-color 0.2s",
                  }}
                >
                  <Box display="flex" justifyContent="space-between" gap={2}>
                    <Tooltip title={s.text} placement="bottom-start" arrow>
                      <Box sx={{ minWidth: 0, flex: 1, cursor: "help" }}>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#555",
                            marginTop: 2,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {primaryText}
                        </div>
                        {s.type === "standardNotation" && (
                          <div style={{ fontSize: 11, color: "#777", marginTop: 4 }}>
                            Best slot: <strong>{matchingAnnotation ? displaySlot : (isSlotCalcPending ? "..." : bestNotationSlot)}</strong>
                          </div>
                        )}
                      </Box>
                    </Tooltip>

                    <Box display="flex" alignItems="center" gap={1} sx={{ whiteSpace: "nowrap" }}>
                      <Box style={{ fontSize: 12, color: "#666" }}>{conf}</Box>

                      {isAutoPlaced ? (
                        <Chip
                          icon={<AutoFixHighIcon style={{ fontSize: 14 }} />}
                          label="Auto Placed"
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 24, fontSize: 11 }}
                        />
                      ) : isManuallyAccepted ? (
                        <Chip
                          label="Accepted"
                          size="small"
                          color="default"
                          variant="filled"
                          sx={{ height: 24, fontSize: 11, backgroundColor: "#e0e0e0" }}
                        />
                      ) : (
                        <Button
                          variant="outlined"
                          size="small"
                          disabled={isSuggestLoading || isSlotCalcPending}
                          onClick={() => applyOneSuggested(s)}
                          sx={{ minWidth: "60px" }}
                        >
                          Accept
                        </Button>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
          </Box>
        ) : null}
      </Box>

      {/* PDF Controls */}
      {numPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2} mb={1} gap={2}>
          <Button
            variant="outlined"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => p - 1)}
          >
            Previous
          </Button>
          <div style={{ marginTop: 8 }}>
            Page {currentPage} of {numPages}
          </div>
          <Button
            variant="outlined"
            disabled={currentPage === numPages}
            onClick={() => setCurrentPage((p) => p + 1)}
          >
            Next
          </Button>
        </Box>
      )}

      <Document file={fileUrl} onLoadSuccess={onDocumentLoadSuccess}>
        <Box className="page-wrapper" ref={pageCanvasRef}>
          <Page
            pageNumber={currentPage}
            width={1000}
            renderTextLayer={false}
            renderAnnotationLayer={false}
            onLoadSuccess={(page) => {
              pageViewBoxRef.current = page?.view || null;
            }}
          />

          <Box className="annotations-layer">
            {annotations
              .filter((a) => a.page === currentPage)
              .map((a) => {
                const isStandardNotation = a.slot?.startsWith("notation");
                const showTitle = a.includeTitle && a.title;
                const isSuggested = !!a.suggested;
                const isAutoPlacedUI = !!a.autoPlacedSuggestionId;

                let bgColor = "rgba(63, 81, 181, 0.9)";
                let textColor = "#fff";
                let borderColor = "1px solid #333";

                if (isAutoPlacedUI) {
                  bgColor = "rgba(46, 125, 50, 0.1)";
                  textColor = "#2e7d32";
                  borderColor = "2px solid #2e7d32";
                } else if (isSuggested) {
                  bgColor = "rgba(25, 118, 210, 0.1)";
                  textColor = "#1976d2";
                  borderColor = "2px solid #1976d2";
                }

                return (
                  <div
                    key={a.id}
                    className="annotation-slot filled"
                    style={{
                      top: a.y,
                      left: a.x,
                      width: a.w,
                      minHeight: a.h, 
                      height: "auto", 
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      position: "absolute",
                      flexDirection: "column",
                      border: borderColor,
                      padding: "6px",
                      backgroundColor: bgColor,
                      color: textColor,
                      zIndex: isAutoPlacedUI ? 1 : 2,
                      wordBreak: "break-word",
                    }}
                  >
                    {isAutoPlacedUI ? (
                      <div
                        style={{
                          fontSize: 9,
                          fontWeight: 700,
                          color: "inherit",
                          marginBottom: 2,
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <AutoFixHighIcon style={{ fontSize: 10 }} /> Auto
                      </div>
                    ) : isSuggested ? (
                      <div style={{ fontSize: 10, fontWeight: 700, color: "inherit", marginBottom: 2 }}>
                        Suggested
                      </div>
                    ) : null}

                    <Tooltip title={a.text} placement="top" arrow>
                      <div
                        style={{
                          fontSize: "0.75rem",
                          lineHeight: "1.2",
                          textAlign: "center",
                          padding: "2px",
                        }}
                      >
                        {showTitle ? <strong>{a.title}: </strong> : null}
                        {a.text}
                      </div>
                    </Tooltip>

                    {isStandardNotation && (
                      <select
                        value={a.slot}
                        onChange={(e) => handleSlotChange(a.id, e.target.value)}
                        style={{
                          marginTop: 4,
                          fontSize: "0.75rem",
                          backgroundColor: "#fff",
                          color: "#000",
                          border: "1px solid #ccc",
                          padding: "2px",
                        }}
                      >
                        <option value="notationA">Notation A</option>
                        <option value="notationB">Notation B</option>
                        <option value="notationC">Notation C</option>
                        <option value="notationD">Notation D</option>
                      </select>
                    )}

                    <Button
                      size="small"
                      onClick={() => handleDeleteAnnotation(a.id)}
                      style={{
                        marginTop: 4,
                        fontSize: "0.6rem",
                        minWidth: "auto",
                        padding: "2px 6px",
                        color: textColor === "#fff" ? "#ffcdd2" : "#d32f2f",
                      }}
                    >
                      ✕
                    </Button>
                  </div>
                );
              })}
          </Box>
        </Box>
      </Document>

      <Box mt={3} display="flex" justifyContent="center">
        <ButtonComponent
          label="Save Annotations"
          type="primary"
          onClick={handleSaveAnnotations}
          isLoading={isLoading}
        />
      </Box>

      {/* Label Date Modal */}
      <Dialog open={labelModalOpen} onClose={handleCancelLabelDate} maxWidth="xs" fullWidth>
        <DialogTitle>Select Label Date</DialogTitle>
        <DialogContent>
          <div style={{ fontSize: 12, color: "#666", marginBottom: 10 }}>
            This label uses a date placeholder. Choose the date to apply to the PDF.
          </div>
          <TextField
            fullWidth
            label="Date"
            type="date"
            value={labelModalDate}
            onChange={(e) => setLabelModalDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCancelLabelDate}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleConfirmLabelDate}>
            Apply Label
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MultiPagePDFViewer; 